import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  FiChevronDown,
  FiChevronUp,
  FiMapPin,
  FiMessageCircle,
  FiSearch,
  FiTag,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import FloatingCartBar from '../../components/floatingCart/FloatingCartBar.tsx';
import { useHeaderVisibility } from '../../components/header/context/HeaderVisibilityContext.tsx';
import Header from '../../components/header/Header.tsx';
import Loading from '../../components/loading/Loading.tsx';
import ConfirmModal from '../../components/modal/ConfirmModal.tsx';
import { fetchActiveDiscount } from '../../discount/discountService.ts';
import { Discount } from '../../discount/discountTypes.ts';
import { useUserContext } from '../../user/context/UserContext.tsx';
import { HOST, ROUTE_NAME_OPTIONS } from '../../utils/constants.ts';
import { capitalizeName, isLogged } from '../../utils/utils.ts';
import { CartItem, useProductContext } from '../context/ProductContext.tsx';
import {
  Product,
  ProductOptionValues,
  ProductType,
  productTypeOptions,
} from '../ProductType.ts';
import { getWeightLabel } from '../utils.ts';
import './product-list.scss';
import ProductRow from './ProductRow.tsx';

const ProductList = () => {
  const {
    setSearchProduct,
    searchProduct,
    filteredProducts,
    addToCart,
    setProducts,
    productTypeFilter,
    setProductTypeFilter,
    shoppingCart,
  } = useProductContext();

  const hasCartBar = isLogged() && shoppingCart.totalItems > 0;
  const cartOptionKeys = new Set(
    shoppingCart.items.map(
      (item) => `${item.product.id}::${item.purchaseOption}`
    )
  );
  const { userInfo } = useUserContext();
  const [activeDiscount, setActiveDiscount] = useState<Discount | null>(null);
  const [cartItems, setCartItems] = useState<{
    [key: string]: CartItem;
  }>({});

  // Opciones de tipo de producto para chips
  const typeFilterOptions = [
    { value: '', label: 'Todos' },
    ...productTypeOptions.map((option) => ({
      value: option.value,
      label: option.label,
    })),
  ];

  // Íconos por categoría para escaneo rápido en los chips
  const categoryIcons: { [key: string]: string } = {
    '': '🧺',
    [ProductType.FRUIT]: '🍎',
    [ProductType.VEGETABLE]: '🥬',
    [ProductType.CONDIMENTS]: '🧂',
    [ProductType.DRIED]: '🥜',
    [ProductType.OTHERS]: '📦',
  };

  const [cardCollapsed, setCardCollapsed] = useState<{
    [key: string]: boolean;
  }>({ zonas: true, consulta: true, descuento: true });
  const toggleCard = (id: string) =>
    setCardCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

  const chipsRef = useRef<HTMLDivElement | null>(null);
  const [chipsAtEnd, setChipsAtEnd] = useState(false);

  useEffect(() => {
    if (!chipsRef.current) return;
    const activeChip = chipsRef.current.querySelector<HTMLButtonElement>(
      '.category-chip.active'
    );
    activeChip?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [productTypeFilter]);

  useEffect(() => {
    const el = chipsRef.current;
    if (!el) return;
    const check = () =>
      setChipsAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
    check();
    el.addEventListener('scroll', check);
    return () => el.removeEventListener('scroll', check);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 20;
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const topRef = useRef<HTMLDivElement | null>(null);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const { forceShowHeader } = useHeaderVisibility();

  const navigate = useNavigate();

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchProduct, productTypeFilter]);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${HOST}/products`);

      if (!response.ok) {
        throw new Error('Error al cargar los productos');
      }

      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [setProducts]);

  useEffect(() => {
    const mainEl = document.querySelector('.main');
    if (mainEl) mainEl.scrollTop = 0;
    window.scrollTo(0, 0);
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchActiveDiscount('efectivo')
      .then((d) => setActiveDiscount(d))
      .catch(() => {});
  }, []);

  const handleCartItemChange = (
    id: string,
    purchaseOption: ProductOptionValues,
    quantity: number,
    productPrice: number
  ) => {
    const product = filteredProducts.find((p) => p.id === id);
    if (!product) return;

    const weight = getWeightLabel(
      product.boxWeight,
      quantity,
      purchaseOption,
      product.productType
    );
    const partialPrice = productPrice;

    setCartItems((prev) => ({
      ...prev,
      [id]: {
        id,
        product,
        purchaseOption,
        quantity,
        weight,
        partialPrice,
        totalPrice: quantity * productPrice,
      },
    }));
  };

  const goToLogin = () => {
    navigate(`/${ROUTE_NAME_OPTIONS.LOGIN}`);
  };

  const handleAddToCart = (product: Product) => {
    if (product.hasStock === false) {
      toast.error('Este producto no tiene stock disponible', {
        duration: 3000,
      });
      return;
    }

    if (!isLogged()) {
      setIsOpenConfirmModal(true);
    } else {
      const cartItem = cartItems[product.id];

      if (
        !cartItem ||
        !cartItem.quantity ||
        isNaN(cartItem.quantity) ||
        cartItem.quantity <= 0
      ) {
        toast.error(
          `Por favor ingresa la cantidad de ${product.name} que desees`,
          { duration: 4000 }
        );
        return;
      }

      addToCart(cartItem);
      toast.custom(
        (t) => (
          <div
            className="toast-custom"
            style={{
              background: '#fff',
              padding: '1rem',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              border: '1px solid #ffa726',
              position: 'relative',
              maxWidth: '90vw',
              minWidth: '320px',
            }}
          >
            <div>
              <strong>✅ {product.name}</strong> fue agregado al carrito
            </div>
            <button
              onClick={() => {
                toast.dismiss();
                navigate(`/${ROUTE_NAME_OPTIONS.SHOPPING_CART}`);
              }}
              className="primary-button"
            >
              Ir al carrito
            </button>
          </div>
        ),
        { duration: 3000 }
      );

      forceShowHeader();
    }
  };

  return (
    <div>
      {isLoading && <Loading />}
      {isOpenConfirmModal && (
        <ConfirmModal
          confirmText={'Ingresa'}
          bodyText={'Necesitas estar logeado para poder realizar tu pedido'}
          action={goToLogin}
          cancel={() => setIsOpenConfirmModal(false)}
        />
      )}
      <Header />
      <div className="product-list-header">
        <div className="flex-justify-between welcome-section">
          {isLogged() && (
            <div className="flex-welcome">
              <h2 className="welcome-title">
                Hola, {capitalizeName(userInfo?.user?.name)}
              </h2>
            </div>
          )}
          <button
            onClick={() => {
              navigate(`/${ROUTE_NAME_OPTIONS.PRICE_LIST}`);
            }}
            className="primary-button"
          >
            Listado precios
          </button>
        </div>

        <div ref={topRef} />
        <div className="info-group info-group--columns">
          <div className="info-group__item">
            <div
              className="info-group__row"
              onClick={() => toggleCard('zonas')}
            >
              <span className="info-card__icon">
                <FiMapPin size={18} />
              </span>
              <span className="info-card__label">Zonas de envío</span>
              {cardCollapsed['zonas'] ? (
                <FiChevronDown size={15} />
              ) : (
                <FiChevronUp size={15} />
              )}
            </div>
            {!cardCollapsed['zonas'] && (
              <div className="info-group__body">
                <span className="info-card__text">
                  Quilmes, Bernal, Ezpeleta, Berazategui, Wilde, Avellaneda,
                  Sarandí. Consultar por otras zonas.
                </span>
              </div>
            )}
          </div>
          <div className="info-group__divider" />
          <div className="info-group__item">
            <div
              className="info-group__row"
              onClick={() => toggleCard('consulta')}
            >
              <span className="info-card__icon">
                <FiMessageCircle size={18} />
              </span>
              <span className="info-card__label">¿No encontrás algo?</span>
              {cardCollapsed['consulta'] ? (
                <FiChevronDown size={15} />
              ) : (
                <FiChevronUp size={15} />
              )}
            </div>
            {!cardCollapsed['consulta'] && (
              <div className="info-group__body">
                <span className="info-card__text">
                  Si deseás un producto que no está en la lista, no dudes en
                  consultarnos.
                </span>
              </div>
            )}
          </div>
          {(!isLogged() || userInfo?.user?.firstPurchase) && (
            <>
              <div className="info-group__divider" />
              <div className="info-group__item">
                <div
                  className="info-group__row"
                  onClick={() => toggleCard('descuento')}
                >
                  <span
                    className="info-card__icon"
                    style={{ color: '#2e7d32' }}
                  >
                    <FiTag size={18} />
                  </span>
                  <span className="info-card__label">
                    🎉 Descuento primera compra
                  </span>
                  {cardCollapsed['descuento'] ? (
                    <FiChevronDown size={15} />
                  ) : (
                    <FiChevronUp size={15} />
                  )}
                </div>
                {!cardCollapsed['descuento'] && (
                  <div className="info-group__body">
                    <span className="info-card__text">
                      Tenés un 10% de descuento total en tu primera compra por
                      la web.
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {activeDiscount?.description && (
          <div className="promo-home-banner">
            <span className="promo-home-banner__icon">🎉</span>
            <span className="promo-home-banner__text">
              {activeDiscount.description}
            </span>
          </div>
        )}

        <h2 className="product-title">Listado de Frutas y Verduras</h2>
        <div className="search-filter-row">
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              className="search-input"
              placeholder="Buscar producto..."
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
            />
          </div>
          <div className={`chips-scroll-wrapper${chipsAtEnd ? ' at-end' : ''}`}>
            <div className="category-chips" ref={chipsRef}>
              {typeFilterOptions.map((option) => (
                <button
                  key={option.value}
                  className={`category-chip ${productTypeFilter === option.value ? 'active' : ''}`}
                  onClick={() => setProductTypeFilter(option.value)}
                >
                  <span className="category-chip__icon" aria-hidden="true">
                    {categoryIcons[option.value]}
                  </span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div
        className={`product-list margin-top-10${hasCartBar ? ' has-cart' : ''}`}
      >
        {currentProducts && currentProducts.length > 0
          ? currentProducts.map((product) => (
              <ProductRow
                key={product.id}
                product={product}
                handleCartItemChange={handleCartItemChange}
                handleAddToCart={handleAddToCart}
                cartOptionKeys={cartOptionKeys}
              />
            ))
          : null}
      </div>
      {(!currentProducts || currentProducts.length === 0) && (
        <p className="no-products-message">
          No hay resultados para tu búsqueda
        </p>
      )}
      <div className={`pagination${hasCartBar ? ' has-cart' : ''}`}>
        {filteredProducts.length > productsPerPage && (
          <div className="product-page-numbers">
            {Array.from(
              { length: Math.ceil(filteredProducts.length / productsPerPage) },
              (_, index) => (
                <button
                  key={index + 1}
                  className={`product-page-number ${
                    currentPage === index + 1 ? 'active' : ''
                  }`}
                  onClick={() => paginate(index + 1)}
                >
                  {index + 1}
                </button>
              )
            )}
          </div>
        )}
      </div>
      {isLogged() && <FloatingCartBar />}
    </div>
  );
};

export default ProductList;
